/* ---------------------------------------------------------------------------
 * Element display duration (dwell time)
 * ---------------------------------------------------------------------------
 * How long ONE element stays on an artboard before the display rotates to the
 * next feature/item. Donations, announcements, Shiurei Torah and posters each
 * have their own org-wide value — `organization.donationDisplayDuration`,
 * `organization.announcementDisplayDuration`,
 * `organization.shiureiTorahDisplayDuration` and
 * `organization.posterDisplayDuration` — stored as a whole number of SECONDS,
 * defaulting to 30 for every organization (the entity column default).
 *
 * The settings modal on each list page enters the value as a number + unit pair
 * (1–60 seconds OR 1–10 minutes); that split is a data-entry convenience only.
 * Everything downstream (Display.vue's carousel, and therefore the Dashboard's
 * embedded selected-screen preview) reads plain seconds through the per-feature
 * getters below, so the two representations can never drift apart.
 *
 * Both features share every rule here on purpose: the two settings modals are
 * meant to look and behave identically.
 * ------------------------------------------------------------------------- */

export type DisplayDurationUnit = 'seconds' | 'minutes';

export const DISPLAY_DURATION_DEFAULT_SECONDS = 30;

/** Widest span a stored value may take: 1 second .. 10 minutes. */
export const DISPLAY_DURATION_MIN_SECONDS = 1;
export const DISPLAY_DURATION_MAX_SECONDS = 600;

export interface DisplayDurationUnitConfig {
  value: DisplayDurationUnit;
  label: string;
  /** Inclusive bounds for the number input while this unit is selected. */
  min: number;
  max: number;
  secondsPerUnit: number;
}

export const DISPLAY_DURATION_UNITS: DisplayDurationUnitConfig[] = [
  { value: 'seconds', label: 'Seconds', min: 1, max: 60, secondsPerUnit: 1 },
  { value: 'minutes', label: 'Minutes', min: 1, max: 10, secondsPerUnit: 60 },
];

export const getDisplayDurationUnitConfig = (
  unit: DisplayDurationUnit
): DisplayDurationUnitConfig =>
  DISPLAY_DURATION_UNITS.find((option) => option.value === unit) || DISPLAY_DURATION_UNITS[0];

/**
 * Coerce any candidate into a valid stored value: a whole number of seconds
 * inside [1, 600]. Anything unusable (null / '' / NaN / 0) falls back to the
 * default rather than to a boundary, so an unset organization still behaves
 * like the documented default.
 *
 * `fallbackSeconds` exists because not every feature shares one: Kabbalah's
 * documented default is 15 seconds where donations, announcements and posters
 * use 30, and an unset organization has to land on its own.
 */
export const clampDisplayDurationSeconds = (
  value: unknown,
  fallbackSeconds: number = DISPLAY_DURATION_DEFAULT_SECONDS
): number => {
  const seconds = Math.round(Number(value));
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return fallbackSeconds;
  }
  return Math.min(
    Math.max(seconds, DISPLAY_DURATION_MIN_SECONDS),
    DISPLAY_DURATION_MAX_SECONDS
  );
};

/**
 * Split stored seconds into the modal's { amount, unit } pair.
 *
 * Whole minutes are shown as minutes (60 -> "1 Minute", 300 -> "5 Minutes");
 * everything else stays in seconds. A value above 60 that is NOT a whole number
 * of minutes can only arrive from outside this UI (a direct API call), so it is
 * rounded to the nearest representable minute — re-saving from the modal then
 * heals the stored value.
 */
export const toDisplayDurationParts = (
  seconds: unknown,
  fallbackSeconds: number = DISPLAY_DURATION_DEFAULT_SECONDS
): { amount: number; unit: DisplayDurationUnit } => {
  const total = clampDisplayDurationSeconds(seconds, fallbackSeconds);

  if (total >= 60) {
    const minutesConfig = getDisplayDurationUnitConfig('minutes');
    const minutes = Math.min(Math.max(Math.round(total / 60), minutesConfig.min), minutesConfig.max);
    return { amount: minutes, unit: 'minutes' };
  }

  return { amount: total, unit: 'seconds' };
};

/**
 * Recombine the modal's { amount, unit } pair back into stored seconds, with
 * the amount clamped into the selected unit's own range first.
 */
export const fromDisplayDurationParts = (
  amount: unknown,
  unit: DisplayDurationUnit
): number => {
  const config = getDisplayDurationUnitConfig(unit);
  const rounded = Math.round(Number(amount));
  const safeAmount = Number.isFinite(rounded)
    ? Math.min(Math.max(rounded, config.min), config.max)
    : config.min;
  return clampDisplayDurationSeconds(safeAmount * config.secondsPerUnit);
};

/**
 * Convert an amount when the user flips the unit dropdown, keeping the duration
 * as close as the new unit allows (30 seconds -> 1 minute, 5 minutes -> 60
 * seconds, the closest each unit can express).
 */
export const convertDisplayDurationAmount = (
  amount: unknown,
  fromUnit: DisplayDurationUnit,
  toUnit: DisplayDurationUnit
): number => {
  const toConfig = getDisplayDurationUnitConfig(toUnit);
  if (fromUnit === toUnit) {
    const rounded = Math.round(Number(amount));
    return Number.isFinite(rounded)
      ? Math.min(Math.max(rounded, toConfig.min), toConfig.max)
      : toConfig.min;
  }

  const fromConfig = getDisplayDurationUnitConfig(fromUnit);
  const totalSeconds = Math.round(Number(amount)) * fromConfig.secondsPerUnit;
  if (!Number.isFinite(totalSeconds)) return toConfig.min;

  const converted = Math.round(totalSeconds / toConfig.secondsPerUnit);
  return Math.min(Math.max(converted, toConfig.min), toConfig.max);
};

/** Human-readable summary, e.g. "30 seconds" / "1 minute" / "5 minutes". */
export const formatDisplayDuration = (seconds: unknown): string => {
  const { amount, unit } = toDisplayDurationParts(seconds);
  const noun = unit === 'minutes' ? 'minute' : 'second';
  return `${amount} ${noun}${amount === 1 ? '' : 's'}`;
};

/* --------------------------- per-feature values --------------------------- */

/** The organization's announcement dwell time in seconds, defaulted + clamped. */
export const getAnnouncementDisplayDurationSeconds = (organization: any): number =>
  clampDisplayDurationSeconds(organization?.announcementDisplayDuration);

/** The organization's announcement dwell time in milliseconds (what a timer wants). */
export const getAnnouncementDisplayDurationMs = (organization: any): number =>
  getAnnouncementDisplayDurationSeconds(organization) * 1000;

/** The organization's Shiurei Torah dwell time in seconds, defaulted + clamped. */
export const getShiureiTorahDisplayDurationSeconds = (organization: any): number =>
  clampDisplayDurationSeconds(organization?.shiureiTorahDisplayDuration);

/** The organization's Shiurei Torah dwell time in milliseconds (what a timer wants). */
export const getShiureiTorahDisplayDurationMs = (organization: any): number =>
  getShiureiTorahDisplayDurationSeconds(organization) * 1000;

/** The organization's donation dwell time in seconds, defaulted + clamped. */
export const getDonationDisplayDurationSeconds = (organization: any): number =>
  clampDisplayDurationSeconds(organization?.donationDisplayDuration);

/** The organization's donation dwell time in milliseconds (what a timer wants). */
export const getDonationDisplayDurationMs = (organization: any): number =>
  getDonationDisplayDurationSeconds(organization) * 1000;

/**
 * The organization's poster dwell time in seconds, defaulted + clamped.
 *
 * This replaced the old poster timing, which multiplied a "rotation speed" read
 * from the DISPLAY DEVICE's localStorage — so a screen that had never visited
 * the Posters page silently fell back to 80s. Poster timing is now org-wide and
 * identical on every screen, exactly like donations and announcements.
 */
export const getPosterDisplayDurationSeconds = (organization: any): number =>
  clampDisplayDurationSeconds(organization?.posterDisplayDuration);

/** The organization's poster dwell time in milliseconds (what a timer wants). */
export const getPosterDisplayDurationMs = (organization: any): number =>
  getPosterDisplayDurationSeconds(organization) * 1000;

/**
 * The organization's Kabbalah dwell time — how long ONE row of Kabbalah cards
 * holds the screen before the next row takes its place.
 *
 * Unlike the three above, an unset organization defaults to 15 seconds: that is
 * what the board ran at before the setting existed, so nothing changes for an
 * organization that never opens the modal.
 */
export const KABBALAH_DISPLAY_DURATION_DEFAULT_SECONDS = 15;

export const getKabbalahDisplayDurationSeconds = (organization: any): number =>
  clampDisplayDurationSeconds(
    organization?.kabbalahDisplayDuration,
    KABBALAH_DISPLAY_DURATION_DEFAULT_SECONDS
  );

/** The same value in milliseconds (what a timer wants). */
export const getKabbalahDisplayDurationMs = (organization: any): number =>
  getKabbalahDisplayDurationSeconds(organization) * 1000;
